import React, { createContext, PropsWithChildren, useContext, useMemo, useState } from "react";
import {
  deleteCurrentUser,
  getCurrentUser,
  logout,
  patchCurrentUser,
  User,
  UserFeature,
  UserFeatureFlag,
  UserSettings,
} from "../api/Users";
import { getLocalStorage, removeLocalStorage, removeSessionStorage, setLocalStorage } from "../utils/local-storage";
import { diff, patch, walk } from "../utils/objects";

type UserContextValue = [UserContextState, UserContextActions?];

interface UserContextRedirectState {
  uri: string;
  state?: {
    params?: { [key: string]: string };
    [key: string]: any;
  };
}

export interface UserContextState {
  status: "init" | "loading" | "failed" | "error" | "ok";
  isAuthenticated: boolean;
  user?: User | null;
  redirect?: UserContextRedirectState;
}

interface UserContextActions {
  load(): Promise<void>;
  logout(): Promise<void>;
  delete(): Promise<void>;
  reset(): void;
  setStatus(status: string): void;
  saveUser(user: User): Promise<void>;
  setFeature(feature: keyof UserSettings, value?: any): Promise<void>;
  toggleFeature(feature: keyof UserFeatureFlag, enabled?: boolean): Promise<void>;
}

const defaultState: UserContextState = {
  status: "init",
  isAuthenticated: false,
  user: getLocalStorage("auth.user", null),
};

// Custom hook
function useUserState(initialState: UserContextState = defaultState): UserContextValue {
  const [state, setState] = useState<UserContextState>(initialState);

  const actions = useMemo(
    () => ({
      load: async () => {
        setState((prevState: UserContextState) => ({
          ...prevState,
          status: "loading",
        }));
        try {
          const res = await getCurrentUser();
          setLocalStorage("auth.user", res.data);

          setState((prevState: UserContextState) => {
            return {
              ...prevState,
              user: res.data,
              isAuthenticated: true,
              status: "ok",
            };
          });
        } catch (err) {
          setState((prevState: UserContextState) => {
            return { ...prevState, isAuthenticated: false, status: "failed" };
          });
          console.warn("Auth failed", err);
        }
      },
      logout: async () => {
        setState((prevState: UserContextState) => ({
          ...prevState,
          status: "loading",
        }));
        removeLocalStorage("auth.user");
        removeSessionStorage("auth.redirect");
        try {
          logout();
          setState((prevState: UserContextState) => {
            return {
              ...prevState,
              user: null,
              isAuthenticated: false,
              status: "ok",
            };
          });
        } catch (err) {
          console.error("Logout failed", err);
          setState((prevState: UserContextState) => {
            return {
              ...prevState,
              user: null,
              isAuthenticated: false,
              status: "error",
            };
          });
        }
      },
      delete: async () => {
        setState((prevState: UserContextState) => ({
          ...prevState,
          status: "loading",
        }));

        // clear user data
        removeLocalStorage("auth.user");
        removeSessionStorage("auth.redirect");
        removeSessionStorage("onboarding.data");

        // clear impersonation data
        removeLocalStorage("auth.admin");
        removeSessionStorage("auth.impersonate");
        removeSessionStorage("auth.impersonate.error");

        try {
          await deleteCurrentUser();
        } catch (err) {
          console.error("Logout failed", err);
          setState((prevState: UserContextState) => {
            return {
              ...prevState,
              user: null,
              isAuthenticated: false,
              status: "error",
            };
          });
        }
      },
      reset: () => {
        // clear user data
        removeLocalStorage("auth.user");
        removeSessionStorage("auth.redirect");
        removeSessionStorage("onboarding.data");

        // clear impersonation data
        removeLocalStorage("auth.admin");
        removeSessionStorage("auth.impersonate");
        removeSessionStorage("auth.impersonate.error");

        // reset state
        setState(initialState);
      },
      setStatus: (status: UserContextState["status"]) => {
        setState((prevState: UserContextState) => ({
          ...prevState,
          status,
        }));
      },

      saveUser: async (user: User) => {
        if (!state.user) {
          console.warn("Cannot save user, user is not loaded");
          return;
        }
        try {
          const payload = diff(state.user, user);
          const res = await patchCurrentUser(payload);
          setLocalStorage("auth.user", res.data);
          setState((prevState) => ({
            ...prevState,
            user: res.data,
          }));
        } catch (err) {
          console.error("Failed to update user", state.user, err);
        }
      },
      setFeature: async <K extends UserFeature>(feature: K, val: UserSettings[K]) => {
        if (!state.user) {
          console.warn("Cannot set feature, user is not loaded", feature);
          return;
        }
        const payload = { features: patch(feature, val) };
        try {
          const res = await patchCurrentUser(payload);
          setLocalStorage("auth.user", res.data);
          setState((prevState) => ({
            ...prevState,
            user: res.data,
          }));
        } catch (err) {
          console.error("Failed to set feature", feature, val, state.user, err);
        }
      },
      toggleFeature: async (feature: keyof UserFeatureFlag, enabled?: boolean) => {
        if (!state.user) {
          console.warn("Cannot toggle feature, user is not loaded", feature);
          return;
        }
        const payload = {
          features: patch(feature, undefined !== enabled ? enabled : !walk(feature, state.user.features)),
        };
        try {
          const res = await patchCurrentUser(payload);
          setLocalStorage("auth.user", res.data);
          setState((prevState) => ({
            ...prevState,
            user: res.data,
          }));
        } catch (err) {
          console.error("Failed to toggle feature", feature, enabled, state.user, err);
        }
      },
    }),
    [initialState, state.user]
  );

  return [state, actions];
}

// Takes the default state in case there is no provider higher in the tree
export const UserContext = createContext<UserContextValue>([defaultState]);

export function UserContextProvider(props: PropsWithChildren<{}>) {
  const [state, actions] = useUserState();

  return <UserContext.Provider value={[state, actions]} {...props} />;
}

// Sub-components can use this component. It will pick up the
// `state` and `actions` given by useUserState() higher in the
// component tree.
export function useUserContext() {
  return useContext<UserContextValue>(UserContext);
}

export default UserContext;
